import type { BDRSummary, SubmissionResult, FailedStep } from "@shared/types";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ExternalLink,
  Copy,
  Plus,
  Link2,
  Sparkles,
  Shield,
  Users,
  Building2,
} from "lucide-react";
import { useState, useEffect } from "react";

interface Props {
  bdr: BDRSummary;
  result: SubmissionResult;
  onSubmitAnother: () => void;
}

const STEP_LABEL: Record<FailedStep, string> = {
  amplemarket: "Amplemarket enrichment",
  sfdc_auth: "Salesforce authentication",
  sfdc_find_account: "Salesforce account lookup",
  sfdc_create_account: "Salesforce account creation",
  sfdc_find_contact: "Salesforce contact lookup",
  sfdc_create_contact: "Salesforce contact creation",
  sfdc_create_opp: "Salesforce opportunity creation",
  sfdc_ocr: "Linking contact to opportunity",
  sfdc_assign_owner: "Assigning AE in Salesforce",
  unknown: "Unknown step",
};

export function Confirmation({ bdr: _bdr, result, onSubmitAnother }: Props) {
  if (result.status === "success") return <SuccessCard result={result} onSubmitAnother={onSubmitAnother} />;
  if (result.status === "duplicate") return <DuplicateCard result={result} onSubmitAnother={onSubmitAnother} />;
  return <FailureCard result={result} onSubmitAnother={onSubmitAnother} />;
}

// ─── Floating particles (celebratory) ────────────────────────────────────────
function SuccessParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 12 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            left: `${10 + Math.random() * 80}%`,
            top: `${10 + Math.random() * 80}%`,
            background: i % 3 === 0 ? "#05FAFC" : i % 3 === 1 ? "#7573F7" : "#C3A0F2",
            boxShadow: `0 0 6px ${i % 3 === 0 ? "#05FAFC" : i % 3 === 1 ? "#7573F7" : "#C3A0F2"}`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

// ─── Animated checkmark ring ─────────────────────────────────────────────────
function CheckRing() {
  return (
    <motion.div
      className="relative w-20 h-20 mx-auto mb-6"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", bounce: 0.4, duration: 0.8, delay: 0.2 }}
    >
      {/* Glow ring */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          background: "conic-gradient(from 0deg, #05FAFC, #7573F7, #C3A0F2, #05FAFC)",
          filter: "blur(8px)",
          opacity: 0.4,
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />
      {/* Inner circle */}
      <div className="absolute inset-[3px] rounded-full bg-[#0A0118] flex items-center justify-center">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.5, type: "spring", bounce: 0.5 }}
        >
          <CheckCircle2 className="w-10 h-10 text-[#05FAFC]" strokeWidth={2} />
        </motion.div>
      </div>
    </motion.div>
  );
}

// ─── SUCCESS ─────────────────────────────────────────────────────────────────
function SuccessCard({ result, onSubmitAnother }: { result: SubmissionResult; onSubmitAnother: () => void }) {
  const [showReceipt, setShowReceipt] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowReceipt(true), 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <SuccessParticles />

      <div className="relative z-10 space-y-6">
        {/* Hero area */}
        <div className="text-center pt-4">
          <CheckRing />
          <motion.h2
            className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#05FAFC] via-white to-[#C3A0F2]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Created in Salesforce
          </motion.h2>
          <motion.p
            className="text-white/40 text-sm mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Stage 0 — Pre-Pipeline · {result.account_name}
          </motion.p>
        </div>

        {/* SFDC button */}
        {result.salesforce_url && (
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <a
              href={result.salesforce_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-[#0A0118] font-bold text-sm shadow-lg shadow-white/10 hover:shadow-white/20 transition-all"
            >
              Open Opportunity in Salesforce
              <ExternalLink size={15} />
            </a>
          </motion.div>
        )}

        {/* Receipt card */}
        <AnimatePresence>
          {showReceipt && (
            <motion.div
              className="backdrop-blur-2xl bg-white/[0.03] rounded-2xl border border-white/[0.06] overflow-hidden"
              initial={{ opacity: 0, y: 20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              transition={{ duration: 0.5 }}
            >
              <div className="p-5 space-y-4">
                {/* Receipt rows */}
                <div className="space-y-3">
                  <ReceiptRow
                    icon={<Building2 className="w-4 h-4" />}
                    label="Account"
                    value={result.account_name ?? "—"}
                    id={result.account_id}
                    tag={result.account_was_new ? "NEW" : "MATCHED"}
                    delay={0}
                  />
                  <ReceiptRow
                    icon={<Users className="w-4 h-4" />}
                    label="Contact"
                    value={result.contact_id ? "Linked" : "—"}
                    id={result.contact_id}
                    tag={result.contact_was_new ? "NEW" : "MATCHED"}
                    delay={0.1}
                  />
                  <ReceiptRow
                    icon={<Sparkles className="w-4 h-4" />}
                    label="Opportunity"
                    value="Discovery (Stage 0)"
                    id={result.opportunity_id}
                    tag="NEW"
                    delay={0.2}
                  />
                  {result.assigned_ae && (
                    <ReceiptRow
                      icon={<Shield className="w-4 h-4" />}
                      label="Assigned AE"
                      value={result.assigned_ae}
                      tag="AUTO"
                      delay={0.3}
                    />
                  )}
                </div>

                {/* Submission ID */}
                <SubmissionIdRow id={result.submission_id} />
              </div>

              {/* Submit another */}
              <div className="border-t border-white/[0.06] p-5">
                <motion.button
                  onClick={onSubmitAnother}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] text-white/70 hover:text-white/90 text-sm font-semibold transition-all"
                >
                  <Plus size={15} />
                  Submit another meeting
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ─── DUPLICATE ───────────────────────────────────────────────────────────────
function DuplicateCard({ result, onSubmitAnother }: { result: SubmissionResult; onSubmitAnother: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Status header */}
      <div className="text-center pt-4">
        <motion.div
          className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E5A100]/10 border border-[#E5A100]/20 flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.4, delay: 0.1 }}
        >
          <AlertTriangle className="w-8 h-8 text-[#E5A100]" strokeWidth={2} />
        </motion.div>
        <h2 className="text-2xl font-extrabold text-white/90">Duplicate Detected</h2>
        <p className="text-white/40 text-sm mt-2">Nothing was sent to Salesforce</p>
      </div>

      <motion.div
        className="backdrop-blur-2xl bg-white/[0.03] rounded-2xl border border-white/[0.06] overflow-hidden"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="p-5 space-y-4">
          <div className="bg-[#E5A100]/[0.06] border border-[#E5A100]/10 rounded-xl p-4">
            <p className="text-sm text-white/70 leading-relaxed">{result.message}</p>
          </div>

          {result.existing_opportunity_url && (
            <a
              href={result.existing_opportunity_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl bg-white text-[#0A0118] font-bold text-sm shadow-lg shadow-white/10 transition-all"
            >
              <Link2 size={15} />
              Open Existing Opportunity
              <ExternalLink size={14} />
            </a>
          )}

          {result.existing_opportunity_id && (
            <div className="bg-white/[0.03] rounded-lg p-3 text-xs font-mono text-white/40 break-all">
              Existing Opp: <span className="text-white/70">{result.existing_opportunity_id}</span>
            </div>
          )}

          <SubmissionIdRow id={result.submission_id} />
        </div>

        <div className="border-t border-white/[0.06] p-5">
          <motion.button
            onClick={onSubmitAnother}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] text-white/70 hover:text-white/90 text-sm font-semibold transition-all"
          >
            <Plus size={15} />
            Submit a different meeting
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── FAILURE ─────────────────────────────────────────────────────────────────
function FailureCard({ result, onSubmitAnother }: { result: SubmissionResult; onSubmitAnother: () => void }) {
  const step = result.failed_at_step ?? "unknown";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Status header */}
      <div className="text-center pt-4">
        <motion.div
          className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.4, delay: 0.1 }}
        >
          <XCircle className="w-8 h-8 text-red-400" strokeWidth={2} />
        </motion.div>
        <h2 className="text-2xl font-extrabold text-white/90">Submission Failed</h2>
        <p className="text-white/40 text-sm mt-2">
          Failed at: <span className="text-red-400 font-semibold">{STEP_LABEL[step]}</span>
        </p>
      </div>

      <motion.div
        className="backdrop-blur-2xl bg-white/[0.03] rounded-2xl border border-white/[0.06] overflow-hidden"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="p-5 space-y-4">
          {/* What happened */}
          <div className="bg-red-500/[0.06] border border-red-500/10 rounded-xl p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-red-400/60 mb-2">What happened</div>
            <p className="text-sm text-white/70 leading-relaxed">{result.message}</p>
          </div>

          {/* Technical error */}
          {result.error_detail && (
            <details className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-xs">
              <summary className="cursor-pointer font-semibold text-white/50 select-none hover:text-white/70 transition-colors">
                Show technical error
              </summary>
              <pre className="mt-3 whitespace-pre-wrap break-all font-mono text-red-400/80 text-[11px] leading-relaxed">
                {result.error_detail}
              </pre>
            </details>
          )}

          {/* Next steps */}
          <div className="bg-[#E5A100]/[0.06] border border-[#E5A100]/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-3.5 h-3.5 text-[#E5A100]/60" />
              <div className="text-xs font-bold uppercase tracking-widest text-[#E5A100]/60">What to do</div>
            </div>
            <div className="text-sm text-white/60 leading-relaxed space-y-1">
              <p>Your submission was saved server-side.</p>
              <p>Notify ops with the Submission ID and error above.</p>
              <p className="text-[#E5A100]/80 font-medium">Do NOT re-submit — it may create a duplicate.</p>
            </div>
          </div>

          <SubmissionIdRow id={result.submission_id} />
        </div>

        <div className="border-t border-white/[0.06] p-5">
          <motion.button
            onClick={onSubmitAnother}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] text-white/70 hover:text-white/90 text-sm font-semibold transition-all"
          >
            <Plus size={15} />
            Submit a different meeting
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Shared components ───────────────────────────────────────────────────────

function ReceiptRow({
  icon, label, value, id, tag, delay = 0,
}: { icon: React.ReactNode; label: string; value: string; id?: string; tag: "NEW" | "MATCHED" | "AUTO"; delay?: number }) {
  return (
    <motion.div
      className="flex items-start gap-3"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.8 + delay, duration: 0.3 }}
    >
      <div className="w-8 h-8 rounded-lg bg-white/[0.05] flex items-center justify-center text-white/30 shrink-0 mt-0.5">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/25 mb-1">
          {label}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white/80 text-sm font-medium">{value}</span>
          <span className={
            tag === "NEW"
              ? "text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-[#05FAFC]/10 text-[#05FAFC]/80 border border-[#05FAFC]/20"
              : tag === "AUTO"
              ? "text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400/80 border border-emerald-500/20"
              : "text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-[#7573F7]/10 text-[#7573F7]/80 border border-[#7573F7]/20"
          }>
            {tag}
          </span>
        </div>
        {id && (
          <div className="text-[10px] font-mono text-white/20 break-all mt-0.5">
            {id}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function SubmissionIdRow({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] pt-4">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/25">
          Submission ID
        </div>
        <div className="font-mono text-xs text-white/50 break-all">{id}</div>
      </div>
      <motion.button
        onClick={copy}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] text-white/40 hover:text-white/60 text-xs font-medium transition-all shrink-0"
      >
        <Copy size={11} />
        {copied ? "Copied!" : "Copy"}
      </motion.button>
    </div>
  );
}
